import { createStore, combineReducers, applyMiddleware, compose, StoreEnhancer, AnyAction, Middleware } from 'redux';
import thunkMiddleware, { ThunkMiddleware } from 'redux-thunk';
import update from 'react-addons-update';
import { devToolsEnhancer } from 'redux-devtools-extension/logOnlyInProduction';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { throttle, get, getConfig, getText } from '../helpers';
import { loadState, saveState } from './web-storage';
import {
    CLEAR_STORE,
    CLEAR_ALL_PROFILES,
    API_CREATE_COLLECTION,
    API_UPDATE_COLLECTION,
    API_DELETE_COLLECTION,
    API_SAVE_TILE_COLLECTIONS,
    API_COPY_COLLECTION
} from './actionTypes';
import SPGVariables from '../utilities/spg-variables';

import { reducer as userReducer, initialUserState } from './user/user.reducer';
import { reducer as datasetsReducer, initialDataSetState } from './datasets/datasets.reducer';
import { reducer as solutionsReducer, initialSolutionsState } from './solutions/solutions.reducer';
import { reducer as blueprintsReducer, initialBlueprintsState } from './blueprints/blueprints.reducer';
import { reducer as filterReducer, initialFiltersState } from './filters/filters.reducer';
import { reducer as errorsReducer, initialErrorState } from './errors/errors.reducer';
import { reducer as sharedReducer, initialSharedState } from './shared/shared.reducer';
import { reducer as queryBuilderReducer, initialQueryBuilderState } from './querybuilder/query-builder.reducer';
import { reducer as queryLibraryReducer, initialQueryLibraryState } from './querylibrary/querylibrary.reducer';
import { updateUserPreferenceManually } from './user/user.actions';
import { reducer as collectionsReducer, initialCollectionState } from './collections/collections.reducer';
import { reducer as announcementsReducer, initialAnnouncementsState } from './announcements/announcements.reducer';
import {
    getAnnouncementSyncStorageKey,
    parseAnnouncementSyncMessage,
    isAnnouncementSyncFromCurrentTab,
    handleAnnouncementSyncMessage,
    hasAnnouncementsSummarySessionLock,
    loadAnnouncementDismissals,
} from './announcements/announcements.storage';
import solutions from '../routes/solutions';
import blueprints from '../routes/blueprints';

const spgVariables = SPGVariables();
const langCode = getText('langCode');

export declare const redux: RootStoreState;

const initialState = {
    version: `${spgVariables.BUILD_VERSION}-${langCode}`,
    user: initialUserState,
    datasets: initialDataSetState,
    solutions: initialSolutionsState,
    blueprints: initialBlueprintsState,
    filters: initialFiltersState,
    errors: initialErrorState,
    shared: initialSharedState,
    querybuilder: initialQueryBuilderState,
    querylibrary: initialQueryLibraryState,
    collectionsList: initialCollectionState,
    announcements: initialAnnouncementsState
};

const reducers = combineReducers<RootStoreState>({
    version: reducerWithInitialState(`${spgVariables.BUILD_VERSION}-${langCode}`),
    user: userReducer,
    datasets: datasetsReducer,
    solutions: solutionsReducer,
    blueprints: blueprintsReducer,
    filters: filterReducer,
    errors: errorsReducer,
    shared: sharedReducer,
    querybuilder: queryBuilderReducer,
    querylibrary: queryLibraryReducer,
    collectionsList: collectionsReducer,
    announcements: announcementsReducer
});

const storeReducer = (state: RootStoreState = initialState, action: AnyAction) => {
    if (action.type === CLEAR_STORE) {
        // Preserve announcements so the panel shows the same state after logout.
        // CLEAR_STORE is dispatched before the logout redirect; without this, the
        // empty state gets written to localStorage and the anon page reload sees an
        // empty list even though the session summary lock prevents a re-fetch.
        // Re-apply any guest dismissals so items the user dismissed before logging in
        // don't reappear once they return to the anonymous state.
        const guestDismissedIds = loadAnnouncementDismissals();
        return update(state, {
            $set: {
                ...initialState,
                announcements: guestDismissedIds.length > 0
                    ? {
                        ...state.announcements,
                        Announcements: state.announcements.Announcements.filter(
                            (item: AnnouncementItem) => !guestDismissedIds.includes(item.KeyMarketplaceAnnouncements)
                        )
                    }
                    : state.announcements
            }
        });
    }
    if (action.type === CLEAR_ALL_PROFILES) {
        return {
            ...state,
            datasets: {
                ...state.datasets,
                full: initialDataSetState.full, // <-- Only clear datasets.full
            },
            solutions: {
                ...state.solutions,
                full: initialSolutionsState.full, // <-- Only clear solutions.full
            },
            blueprints: {
                ...state.blueprints,
                full: initialBlueprintsState.full, // <-- Only clear blueprints.full
            },
        };
    }
    return reducers(state, action);
};

// ---------------------------------------------------------------------------
// Cross-tab collection sync via BroadcastChannel
// ---------------------------------------------------------------------------
const COLLECTION_SYNC_ACTIONS = new Set([
    API_CREATE_COLLECTION,
    API_UPDATE_COLLECTION,
    API_DELETE_COLLECTION,
    API_SAVE_TILE_COLLECTIONS,
    API_COPY_COLLECTION,
]);

const collectionsSyncChannel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('collections_sync')
    : null;

const collectionsSyncMiddleware: Middleware<Record<string, never>, RootStoreState> = () => next => (action: AnyAction) => {
    const result = next(action);
    // Broadcast the action to other tabs, but never re-broadcast what came from another tab
    if (collectionsSyncChannel && COLLECTION_SYNC_ACTIONS.has(action.type) && !action.fromBroadcast) {
        collectionsSyncChannel.postMessage({ type: action.type, response: action.response });
    }
    return result;
};
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Cross-tab announcements dismissal sync via BroadcastChannel + sessionStorage
// ---------------------------------------------------------------------------
const announcementsSyncChannel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('announcements_sync')
    : null;

const announcementsSyncMiddleware: Middleware<Record<string, never>, RootStoreState> = storeApi => next => (action: AnyAction) => {
    const result = next(action);
    // Broadcast dismissals to other tabs, but never re-broadcast what came from another tab
    if (announcementsSyncChannel && action.type === 'API_DISMISS_ANNOUNCEMENTS' && !action.fromBroadcast) {
        const isLoggedIn = get(storeApi.getState(), 'user.isLoggedIn', false);
        announcementsSyncChannel.postMessage({
            type: 'DISMISS',
            announcementIds: action.announcementIds,
            isLoggedIn
        });
    }
    return result;
};
// ---------------------------------------------------------------------------

const thunk: ThunkMiddleware<RootStoreState, AnyAction> = thunkMiddleware;
const middlewareEnhancer = applyMiddleware(thunk, collectionsSyncMiddleware, announcementsSyncMiddleware);

const composedEnhancers: StoreEnhancer = compose(
    middlewareEnhancer,
    devToolsEnhancer({} as Record<string, never>)
);

let persistedState: RootStoreState | undefined;

if (!spgVariables.IS_SERVER_REQUEST) {
    persistedState = process.env.REACT_APP_IGNORE_LOCALSTORAGE !== 'true' && !spgVariables.IS_CRAWLER_REQUEST ? loadState() : undefined;
}

if (!spgVariables.IS_SERVER_REQUEST && redux) {
    if (!(persistedState === undefined)) {
        persistedState = update(persistedState, { $merge: redux }) as RootStoreState;
    } else {
        persistedState = redux;
    }
}
// TODO remove this any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const store = createStore<RootStoreState, AnyAction, any, any>(storeReducer, persistedState as any, composedEnhancers);

if (!spgVariables.IS_CRAWLER_REQUEST && !spgVariables.IS_SERVER_REQUEST) {
    store.subscribe(() => saveState(store.getState()));
}

// window is not available when this module is required by the SSR bundle in Node.js.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
        const bookmarkPreferenceFromLS = localStorage.getItem('bookmarkPreference');
        const announcementSyncStorageKey = getAnnouncementSyncStorageKey();

        if (event.key === announcementSyncStorageKey && event.newValue) {
            const message = parseAnnouncementSyncMessage(event.newValue);
            if (!isAnnouncementSyncFromCurrentTab(message)) {
                handleAnnouncementSyncMessage(message || {}, store.dispatch, announcementsSyncChannel);
            }
        }

        if (bookmarkPreferenceFromLS && bookmarkPreferenceFromLS !== JSON.stringify(store.getState().user.bookmarkPreference)) {
            store.dispatch(updateUserPreferenceManually.action(JSON.parse(bookmarkPreferenceFromLS)));
            localStorage.removeItem('bookmarkPreference');
            localStorage.removeItem('collectionsList');
        }
    });
}

// Receive collection mutations broadcast from other tabs and replay them locally
if (collectionsSyncChannel) {
    collectionsSyncChannel.onmessage = (event: MessageEvent) => {
        const { type, response } = event.data || {};
        if (type && COLLECTION_SYNC_ACTIONS.has(type)) {
            store.dispatch({ type, response, fromBroadcast: true });
        }
    };
}

// Initialize announcements dismissal sync on tab startup
if (announcementsSyncChannel && typeof window !== 'undefined') {
    // Set up listener for messages from other tabs
    announcementsSyncChannel.onmessage = (event: MessageEvent) => {
        if ((event.data || {}).type === 'SUMMARY_FETCH_STATUS_REQUEST') {
            announcementsSyncChannel.postMessage({
                type: 'SUMMARY_FETCH_STATUS_RESPONSE',
                requestId: (event.data || {}).requestId,
                fetched: hasAnnouncementsSummarySessionLock()
            });
            return;
        }

        handleAnnouncementSyncMessage(event.data || {}, store.dispatch, announcementsSyncChannel);
    };

    // Send INIT request to see if there are other tabs with dismissed announcements
    announcementsSyncChannel.postMessage({ type: 'INIT_REQUEST' });
}

export {
    store as default,
    initialState
};
